import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PageHeaderComponent } from '@shared/components/layout';
import { UnitService } from '../../services/unit.service';
import { OrganizationUnit, ParentChangeHistory } from '../../models/unit.model';
import { BreadcrumbService } from '@core/services/breadcrumb.service';

@Component({
  selector: 'app-unit-parent-history-page',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
    PageHeaderComponent,
  ],
  templateUrl: './unit-parent-history-page.component.html',
  styleUrl: './unit-parent-history-page.component.scss',
})
export class UnitParentHistoryPageComponent implements OnInit, OnDestroy {
  unit: OrganizationUnit | null = null;
  history: ParentChangeHistory[] = [];
  isLoading = true;
  errorMessage: string | null = null;

  readonly displayedColumns = ['effectiveDate', 'fromUnit', 'toUnit', 'reason', 'registeredAt'];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly unitService: UnitService,
    private readonly cdr: ChangeDetectorRef,
    private readonly breadcrumbService: BreadcrumbService,
  ) {}

  ngOnInit(): void {
    const allParams = this.route.snapshot.pathFromRoot.reduce(
      (acc, r) => ({ ...acc, ...r.params }), {} as Record<string, string>
    );
    const unitId = allParams['unit-id'];
    this.loadData(unitId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(unitId: string): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.unitService.getUnit(unitId).pipe(takeUntil(this.destroy$)).subscribe({
      next: unit => {
        this.unit = unit;
        this.breadcrumbService.setDynamicCrumb('unit-id', unit.name);
        this.loadHistory(unitId);
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'No se pudo cargar la unidad.';
        this.cdr.markForCheck();
      },
    });
  }

  private loadHistory(unitId: string): void {
    this.unitService.getParentChangeHistory(unitId).pipe(takeUntil(this.destroy$)).subscribe({
      next: history => {
        this.history = history;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'No se pudo cargar el historial.';
        this.cdr.markForCheck();
      },
    });
  }

  navigateToUnit(unitId: string | null): void {
    if (unitId) {
      this.router.navigate(['/units', unitId]);
    }
  }
}
